import React, { type ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAidiAccess } from 'src/utils/hooks/useAidiAccess';
import { Spinner } from '../Spinner';
import styles from './AidiWrapper.module.scss';
import { useNavigateWithSecureParams } from 'src/utils/hooks/useNavigateWithSecureParams';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface AidiWrapperProps {
  children: ReactNode;
}

const AidiWrapper: React.FC<AidiWrapperProps> = ({ children }) => {
  const [isLdUserIdentified, setIsLdUserIdentified] = useState(false);
  const [hasAccessError, setHasAccessError] = useState(false);
  const { getParam } = useNavigateWithSecureParams();
  const [caseId, setCaseId] = useState<number | null>(null);
  const caseIdFromParams = getParam('caseId');
  const flags = useFlags();
  const location = useLocation();
  const useUrlCaseId = flags['fnx-show-backtodocuments'] ?? false;
  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    if (pathSegments[1] === 'aidi' && pathSegments[2] === 'case' && pathSegments[3] && useUrlCaseId) {
      const extractedCaseId = parseInt(pathSegments[3], 10);
      if (!isNaN(extractedCaseId)) {
        setCaseId(extractedCaseId);
      }
    }
    else {
        setCaseId(caseIdFromParams ? parseInt(caseIdFromParams, 10) : null);
    }
  }, [location.pathname, useUrlCaseId, caseIdFromParams]);

  

  const { loading, error: accessError, shouldShowAidiEntry } = useAidiAccess({ caseId });

  // Mirror previous local dev override behavior
  const effectiveAccessGranted = process.env.NODE_ENV === 'development' ? true : shouldShowAidiEntry;

  useEffect(() => {
    if (effectiveAccessGranted) {
      setIsLdUserIdentified(true);
      setHasAccessError(false);
    } else if (!loading && !effectiveAccessGranted) {
      setHasAccessError(true);
    }
  }, [effectiveAccessGranted, loading]);

  // Show loading state while case details are being fetched or LD is identifying
  if (loading || (!isLdUserIdentified && !accessError)) {
    return (
      <div className={styles['aidi-wrapper']}>
        <Spinner />
      </div>
    );
  }

  // Show 404 error if feature flag check fails
  if (hasAccessError || accessError) {
    return (
      <div className={styles['aidi-wrapper']}>
        <div className={styles['not-found-container']}>
          <div className={styles['not-found-title']}>404</div>
          <div className={styles['not-found-message']}>Page Not Found</div>
        </div>
      </div>
    );
  }

  // Render AIDI component if access is granted
  return <div className={styles['aidi-wrapper']}>{children}</div>;
};

export default AidiWrapper;
